<template>
  <div>
    <ContentWrap title="评论管理">
      <el-form :inline="true" class="mb8">
        <el-form-item label="直播间编号">
          <LiveRoomIdSelect v-model="q.liveId" select-class="!w-260px" />
        </el-form-item>
        <el-form-item label="评论用户">
          <LiveMemberUserSelect v-model="q.userId" select-class="!w-260px" />
        </el-form-item>
        <el-form-item label="评论内容">
          <el-input :maxlength="200" v-model="q.content" placeholder="请输入评论内容" clearable />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="q.status" clearable class="!w-140px">
            <el-option label="显示" :value="1" />
            <el-option label="隐藏" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="query"><Icon icon="ep:search" />查询</el-button>
        </el-form-item>
      </el-form>
    </ContentWrap>

    <ContentWrap>
      <el-table v-loading="loading" :data="list" border>
        <el-table-column label="编号" prop="id" width="80" />
        <el-table-column label="直播编号" prop="liveId" width="100" align="center" />
        <el-table-column label="直播间" min-width="180">
          <template #default="scope">{{
            roomNames[scope.row.liveId] || `直播 #${scope.row.liveId}`
          }}</template>
        </el-table-column>
        <el-table-column label="评论用户" min-width="150">
          <template #default="scope">{{
            userNames[scope.row.userId] || `用户 #${scope.row.userId}`
          }}</template>
        </el-table-column>
        <el-table-column label="评论内容" prop="content" min-width="200" show-overflow-tooltip />
        <el-table-column label="状态" prop="status" width="80">
          <template #default="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'">{{
              scope.row.status === 1 ? '显示' : '隐藏'
            }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column
          label="创建时间"
          prop="createTime"
          min-width="180"
          :formatter="dateFormatter"
        />
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="scope">
            <el-button
              link
              type="primary"
              @click="toggleStatus(scope.row)"
              v-hasPermi="['live:comment:update']"
              >{{ scope.row.status === 1 ? '隐藏' : '显示' }}</el-button
            >
            <el-button
              link
              type="danger"
              @click="handleDelete(scope.row)"
              v-hasPermi="['live:comment:delete']"
              >删除</el-button
            >
          </template>
        </el-table-column>
      </el-table>

      <pagination
        v-show="total > 0"
        :total="total"
        v-model:page="q.pageNo"
        v-model:limit="q.pageSize"
        @pagination="getList"
      />
    </ContentWrap>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { pageComment, updateCommentStatus, deleteComment } from '@/api/live/comment'
import { getRoomPage } from '@/api/live/room'
import { getUserPage } from '@/api/member/user'
import { dateFormatter } from '@/utils/formatTime'
import LiveRoomIdSelect from '@/views/live/components/LiveRoomIdSelect.vue'
import LiveMemberUserSelect from '@/views/live/components/LiveMemberUserSelect.vue'

const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const roomNames = reactive<Record<number, string>>({})
const userNames = reactive<Record<number, string>>({})

const q = reactive({
  pageNo: 1,
  pageSize: 10,
  liveId: undefined as number | undefined,
  userId: undefined as number | undefined,
  content: '',
  status: undefined as number | undefined
})

const getList = async () => {
  loading.value = true
  try {
    const data: any = await pageComment(q)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const query = () => {
  q.pageNo = 1
  getList()
}

/** 加载直播间和会员名称，避免列表只显示业务编号。 */
const loadLookups = async () => {
  const [rooms, users] = await Promise.all([
    getRoomPage({ pageNo: 1, pageSize: 100 }),
    getUserPage({ pageNo: 1, pageSize: 100 })
  ])
  ;(rooms.list || []).forEach((room: any) => (roomNames[room.id] = room.title))
  ;(users.list || []).forEach(
    (user: any) => (userNames[user.id] = user.nickname || user.mobile || `用户 #${user.id}`)
  )
}

const toggleStatus = async (row: any) => {
  const status = row.status === 1 ? 0 : 1
  await updateCommentStatus(row.id, status)
  ElMessage.success('操作成功')
  getList()
}

const handleDelete = (row: any) => {
  ElMessageBox.confirm('确认删除该评论吗？', '提示', { type: 'warning' }).then(async () => {
    await deleteComment(row.id)
    ElMessage.success('删除成功')
    getList()
  })
}

onMounted(() => {
  getList()
  loadLookups()
})
</script>
